import { useEffect, useRef, useState } from 'react';
import { Autoplay, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import tailwindBreakpoints from 'Utils/tailwindBreakpoints';
import { BreakPointHooks } from 'Hooks/BreakPointHooks';

import {
    IProductItemViewModel,
    IProductSliderBlockViewModel,
} from 'Types/Litium';
import Markup from 'Components/Markup';
import ProductCard from 'Components/ProductCard/ProductCard';

import ProductSliderNavigation from './ProductSliderNavigation';

const { useGreater } = BreakPointHooks();

interface IProductSlider
    extends Omit<IProductSliderBlockViewModel, 'colorRange' | 'freeText'> {
    title?: string;
    freeText?: IProductSliderBlockViewModel['freeText'];
    shouldLazyLoad?: boolean;
    showSizes?: boolean;
}

const ProductSlider = ({
    showSizes = true,
    title,
    freeText,
    products,
    itemsToShowDesktop = 3,
    itemsToShowMobile = 2,
    marginBetweenSlidesDesktop = 10,
    marginBetweenSlidesMobile = 3,
    translations,
    showArrows = true,
    autoSlide = false,
    textWidth = 100,
    autoSlideTime = 5,
    shouldLazyLoad = false,
}: IProductSlider) => {
    const swiperRef = useRef<SwiperClass | null>(null);
    const [, setSignal] = useState({});
    const forceRerender = () => setSignal({});

    const [slides, setSlides] = useState<IProductItemViewModel[]>(products);
    const isGreater = useGreater('lg');
    const numbersToShow =
        itemsToShowDesktop > itemsToShowMobile
            ? itemsToShowDesktop
            : itemsToShowMobile;
    const handleMouseEnter = () => {
        swiperRef.current?.autoplay?.pause();
    };

    const handleMouseLeave = () => {
        swiperRef.current?.autoplay?.resume();
    };

    useEffect(() => {
        setSlides(products);
    }, [products]);

    useEffect(() => {
        if (swiperRef.current) {
            swiperRef.current.on('slideChange', forceRerender);
            return () => {
                swiperRef.current.off('slideChange', forceRerender);
            };
        }
    }, [swiperRef]);

    useEffect(() => {
        const items = isGreater ? itemsToShowDesktop : itemsToShowMobile;
        if (products.length <= items) {
            setSlides(products);
        } else {
            const meetLoopSlidesCriteria =
                items * 2 > products.length
                    ? [...products, ...products]
                    : products;
            setSlides(meetLoopSlidesCriteria);
        }
    }, [isGreater]);

    const imageAspectRatio = 1.43;
    const srcSetSizes = {
        default: {
            width: Math.round(
                parseInt(tailwindBreakpoints.sm) / itemsToShowMobile
            ),
            height: Math.round(
                (parseInt(tailwindBreakpoints.sm) / itemsToShowMobile) *
                    imageAspectRatio
            ),
        },
        md: {
            width: Math.round(
                parseInt(tailwindBreakpoints.md) / itemsToShowMobile
            ),
            height: Math.round(
                (parseInt(tailwindBreakpoints.md) / itemsToShowMobile) *
                    imageAspectRatio
            ),
        },
        lg: {
            width: Math.round(
                parseInt(tailwindBreakpoints.lg) / itemsToShowDesktop
            ),
            height: Math.round(
                (parseInt(tailwindBreakpoints.lg) / itemsToShowDesktop) *
                    imageAspectRatio
            ),
        },
        xl: {
            width: Math.round(
                parseInt(tailwindBreakpoints.xl) / itemsToShowDesktop
            ),
            height: Math.round(
                (parseInt(tailwindBreakpoints.xl) / itemsToShowDesktop) *
                    imageAspectRatio
            ),
        },
        xxl: {
            width: Math.round(
                parseInt(tailwindBreakpoints.xxl) / itemsToShowDesktop
            ),
            height: Math.round(
                (parseInt(tailwindBreakpoints.xxl) / itemsToShowDesktop) *
                    imageAspectRatio
            ),
        },
    };

    return (
        <div className="relative flex w-full max-w-full flex-row flex-wrap justify-around">
            {(title || freeText?.value) && (
                <div className="basis-full">
                    {title && (
                        <div className="mb-4 basis-full text-center">
                            <h2 className="font-montserrat text-2xl font-normal text-black">
                                {title}
                            </h2>
                        </div>
                    )}
                    {freeText?.value && (
                        <Markup
                            html={freeText?.value}
                            cssClass="relative mx-auto lg:w-[var(--block-text-width)]"
                            style={
                                {
                                    '--block-text-width': `${textWidth}%`,
                                } as React.CSSProperties
                            }
                        />
                    )}
                </div>
            )}
            <div
                className="relative w-full"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseEnter}
            >
                <Swiper
                    onSwiper={swiper => {
                        swiperRef.current = swiper;
                        forceRerender();
                    }}
                    className="relative flex w-full [&.swiper-initialized_.swiper-slide:not(.swiper-slide-visible)]:invisible"
                    modules={[Navigation, Autoplay, Pagination, Keyboard]}
                    userAgent="Server"
                    slidesPerView="auto"
                    speed={600}
                    watchSlidesProgress
                    autoplay={
                        autoSlide && {
                            delay: autoSlideTime * 1000,
                            disableOnInteraction: true,
                        }
                    }
                    keyboard={{
                        enabled: true,
                    }}
                    autoHeight={false}
                    loop
                    wrapperClass={`flex ${
                        slides.length <= itemsToShowDesktop
                            ? 'lg:justify-center'
                            : ''
                    }`}
                    wrapperTag="ul"
                    lazyPreloaderClass="custom-preloader"
                >
                    {slides.map((item, index) => (
                        <SwiperSlide
                            tag="li"
                            key={index}
                            style={
                                {
                                    '--slide-margin': `${marginBetweenSlidesDesktop}px`,
                                    '--slide-width': `calc(${
                                        100 / itemsToShowDesktop
                                    }% + ${
                                        marginBetweenSlidesDesktop /
                                        itemsToShowDesktop
                                    }px	)`,
                                    '--slide-margin-mobile': `${marginBetweenSlidesMobile}px`,
                                    '--slide-width-mobile': `calc(${
                                        100 / itemsToShowMobile
                                    }% + ${
                                        marginBetweenSlidesMobile /
                                        itemsToShowMobile
                                    }px	)`,
                                } as React.CSSProperties
                            }
                            className="basis-[var(--slide-width-mobile)] pr-[var(--slide-margin-mobile)] transition-[visibility] duration-[600ms] last:-mr-[var(--slide-margin-mobile)] lg:basis-[var(--slide-width)] lg:pr-[var(--slide-margin)] lg:last:-mr-[var(--slide-margin)]"
                        >
                            <ProductCard
                                showSizes={showSizes}
                                images={item.images}
                                name={item.name}
                                url={item.url}
                                translations={item.translations}
                                videoUrl={item.videoUrl}
                                lazy={
                                    shouldLazyLoad ||
                                    (!shouldLazyLoad && index > numbersToShow)
                                }
                                srcSetSizes={srcSetSizes}
                                productTag={item.productTag}
                                priceVM={item.priceVM}
                                hasAnyVariantEnabled={item.hasAnyVariantEnabled}
                                productFilters={item.productFilters}
                                isOnWishList={item.isOnWishList}
                                enabledWishlist={item.enabledWishlist}
                                isPurchasable={item.isPurchasable}
                                id={item.id}
                                showQuantityField={item.showQuantityField}
                                descriptiveVariantName={
                                    item.descriptiveVariantName
                                }
                                trackingData={item.trackingData}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
                {showArrows && (
                    <ProductSliderNavigation
                        realIndex={swiperRef.current?.realIndex}
                        swiper={swiperRef.current}
                        slides={slides}
                        itemsToShowDesktop={itemsToShowDesktop}
                        itemsToShowMobile={itemsToShowMobile}
                        translationNextSlide={translations.nextSlide}
                        translationPreviousSlide={translations.previousSlide}
                    />
                )}
            </div>
        </div>
    );
};

export default ProductSlider;
